import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import ErrorIcon from '@mui/icons-material/Error'

import CategoryNav from '@/components/menu/CategoryNav'
import CartSidebar from '@/components/menu/CartSidebar'
import { CartBottomBar } from '@/components/cart/CartBottomBar'
import MenuHeader from '@/components/menu/MenuHeader'
import MenuPage from '@/pages/menu/MenuPage'
import { StoreClosed } from '@/components/StoreClosed'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	selectSelectedMenuSchedule,
	selectIsDeferred,
	selectExpectedTimeUtc,
	selectOrderTimeUtc,
	selectSelectedOrderTypeGuid,
	selectCartItems,
	selectDineInTableName,
	selectDineInAllowDuringOffHours,
	selectCalculateErrors,
	initMenuSchedule,
	calculate,
	clearCart,
	setItemsRemovedCount,
	setMenuSchedule,
	setSchedule,
	setStoreSettingsChangedError,
	removeItemsByMenuRef,
} from '@/store/ticketSlice'
import { useScrollCompact } from '@/hooks/useScrollCompact'
import { useLocationRef } from '@/contexts/RefContext'
import { getSchedules } from '@/shared/api/timeSlots'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { PATHS } from '@/router/paths'
import { useFormatMessage } from '@/translations/useFormatMessage'
import type { LocationData, MenuSchedule } from '@/shared/api/location'
import type { MenuCategory } from '@/shared/api/menu'
import { useMenuCategories } from '@/pages/menu/hooks/useMenuCategories'
import { useOrderAgain } from '@/pages/menu/hooks/useOrderAgain'
import { NoActiveSchedule, SCHEDULE_UNAVAILABLE_CODES } from '@/components/NoActiveSchedule'
import { toI18nKey } from '@/lib/toI18nKey'
import {
	LayoutGrid,
	MainContent,
	StickyHeader,
	CartSidebarSticky,
	CalculateErrorBanner,
	CalculateErrorText,
	OffHoursWarningBanner,
	OffHoursWarningText,
	ScheduleUnavailableStatusIcon,
} from './MenuLayout.styled'

export interface MenuLayoutContext {
	location: LocationData | undefined
	scheduleId: string
	searchTerm: string
	onClearSearch: () => void
	isStoreClosureActive?: boolean
	hasSameDayClosure?: boolean
}

export interface MenuLayoutOutletContext {
	location: LocationData
	isStoreClosureActive?: boolean
	activeStoreClosureMessage?: string | null
	hasSameDayClosure?: boolean
}

const SCHEDULE_UNAVAILABLE_KEYS = new Set(
	[...SCHEDULE_UNAVAILABLE_CODES].map((code) => toI18nKey('olo:menu', code)),
)

export default function MenuLayout() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { t } = useFormatMessage()
	const outletContext = useOutletContext<MenuLayoutOutletContext | null>()
	const [searchTerm, setSearchTerm] = useState('')
	const clearSearchRef = useRef<(() => void) | null>(null)
	const menuHeaderRef = useRef<HTMLDivElement | null>(null)

	const location = outletContext?.location
	const isStoreClosureActive = outletContext?.isStoreClosureActive ?? false
	const hasSameDayClosure = outletContext?.hasSameDayClosure ?? false
	const isStoreClosed = location?.isClosed ?? false
	const showStoreClosed = isStoreClosureActive || isStoreClosed

	const selectedMenuSchedule = useAppSelector(selectSelectedMenuSchedule)
	const selectedScheduleRef = selectedMenuSchedule?.ref
	const [searchParams] = useSearchParams()
	const urlScheduleRefs = useMemo(
		() => new Set(searchParams.getAll('schedules[]')),
		[searchParams],
	)
	const ref = useLocationRef()
	const selectedOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)
	const isDineIn = !selectedOrderTypeGuid || selectedOrderTypeGuid === 'DINE_IN'
	const isDeferred = useAppSelector(selectIsDeferred)
	const expectedTimeUtc = useAppSelector(selectExpectedTimeUtc)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const dineInAllowDuringOffHours = useAppSelector(selectDineInAllowDuringOffHours)
	const orderTimeUtc = useAppSelector(selectOrderTimeUtc)

	const filteredBootstrapSchedules = useMemo((): MenuSchedule[] => {
		const schedules = location?.menuSchedules ?? []
		if (urlScheduleRefs.size === 0) return schedules
		return schedules.filter((s) => urlScheduleRefs.has(s.ref))
	}, [location, urlScheduleRefs])

	// Ember parity (menu.js getSchedules path 3): deferred non-dine-in uses availability/schedules API; ASAP/dine-in use bootstrap menuSchedules unchanged.
	// Sends orderTimeUtc (prep time), matching Ember, which passes getOrderTime()'s result — slot.orderTime,
	// or expectedTime minus distanceLeadTime for DSP — at every one of its three call sites. ConfirmButton
	// must send the same value so both resolve the same schedule and neither evicts the other's selection.
	const { data: availableSchedulesData } = useQuery({
		queryKey: ['available-schedules', ref, selectedOrderTypeGuid, orderTimeUtc],
		queryFn: () => getSchedules(ref, selectedOrderTypeGuid!, orderTimeUtc!),
		enabled: isDeferred && !isDineIn && !!orderTimeUtc && !!selectedOrderTypeGuid,
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	const headerSchedules = useMemo((): MenuSchedule[] => {
		if (!location) return []
		if (!isDeferred || isDineIn) {
			// STO-3834: bootstrap returns off-hours schedules for ?dineIn=true regardless of the
			// venue's setting, so the client decides. Listing them when the venue has NOT enabled
			// "Allow Ordering Outside Of Schedule Hours" would be a dead end —
			// selectIsScheduleAvailable blocks checkout for exactly isDineIn && duringOffHours.
			// Diverges from Ember, which lists them unconditionally (see docs/code-review-STO-3834.md).
			// ASAP non-dine-in (STO-3811): never shown.
			const allowOffHoursSchedules = isDineIn && dineInAllowDuringOffHours
			return allowOffHoursSchedules
				? filteredBootstrapSchedules
				: filteredBootstrapSchedules.filter((s) => !s.duringOffHours)
		}
		if (!availableSchedulesData) return filteredBootstrapSchedules
		// API returns ref+name only; merge with bootstrap for full hours. Empty result means API excluded all — do not fall back.
		const merged = availableSchedulesData.schedules.map((s) => {
			const full = location.menuSchedules.find((ls) => ls.ref === s.ref)
			return full ?? { ref: s.ref, name: s.name, hours: [], duringOffHours: false }
		})
		if (urlScheduleRefs.size === 0) return merged
		return merged.filter((s) => urlScheduleRefs.has(s.ref))
	}, [
		isDeferred,
		isDineIn,
		dineInAllowDuringOffHours,
		availableSchedulesData,
		location,
		filteredBootstrapSchedules,
		urlScheduleRefs,
	])

	// True while the deferred availability API has not yet responded. During this window bootstrap
	// is used as a temporary source (so scheduleId stays valid for useMenuCategories) and no stored
	// selection is evicted — it may exist only in the API result and would be falsely rejected
	// against bootstrap alone (STO-3811).
	const deferredAvailabilityPending =
		isDeferred && !isDineIn && availableSchedulesData === undefined
	const urlFilterActive = urlScheduleRefs.size > 0 && location !== undefined
	// Use headerSchedules (off-hours-filtered and API-enriched) as the authoritative source so a
	// URL-selected schedule that is duringOffHours is correctly treated as unavailable (STO-3811).
	const hasInvalidUrlSchedule =
		urlFilterActive && !deferredAvailabilityPending && headerSchedules.length === 0

	// While deferred availability is pending, keep bootstrap as the temporary source so
	// useMenuCategories always has a valid ref. Once the API resolves, switch to headerSchedules.
	const scheduleSources = deferredAvailabilityPending
		? filteredBootstrapSchedules
		: headerSchedules
	// Ember parity (menu.js getSchedules + filteredSchedules): the schedule driving the menu data
	// query is never filtered by duringOffHours — filteredSchedules only applies the URL schedules
	// param. On both redirect-to-order-settings paths Ember still returns location.menuSchedules
	// (menu.js:204 ASAP-unavailable, menu.js:223 no-deferred-schedules) so categories and items
	// load behind the modal. headerSchedules stays off-hours-filtered for the pills (STO-3811);
	// without this fallback scheduleId is '' before a schedule starts, which disables
	// useMenuCategories/useMenuItems and leaves an empty page behind the overlay (STO-3833).
	const scheduleId =
		selectedScheduleRef !== undefined &&
		scheduleSources.some((s) => s.ref === selectedScheduleRef)
			? selectedScheduleRef
			: (scheduleSources[0]?.ref ?? filteredBootstrapSchedules[0]?.ref ?? '')
	const isCompact = useScrollCompact(menuHeaderRef)

	const cartItems = useAppSelector(selectCartItems)

	// Ember parity: default to first schedule so menuScheduleRef is never null for calculate(). initMenuSchedule (not setMenuSchedule) avoids overwriting a stored schedule that can't be matched at restore time.
	// When the stored selection is absent from the valid schedule list (URL-excluded, deleted, or
	// absent from the availability API response), replace it with the first filtered schedule.
	useEffect(() => {
		const resolved = deferredAvailabilityPending ? filteredBootstrapSchedules : headerSchedules
		if (!selectedMenuSchedule) {
			// Ember menu.js:42-48 sets menuScheduleRef to menuSchedules.firstObject whenever none is
			// stored, with no off-hours exclusion. Mirror the scheduleId fallback so calculate() still
			// has a schedule ref while the menu is browsable before its schedule starts (STO-3833).
			// Only the no-selection branch falls back — the eviction branch below keeps its stricter
			// list so a stored selection is never replaced by an off-hours one (STO-3811).
			const first = resolved[0] ?? filteredBootstrapSchedules[0]
			if (first) {
				dispatch(initMenuSchedule(first))
			}
			return
		}
		// For deferred non-dine-in: wait for the availability API before evicting — the stored ref
		// may be API-only and would be falsely rejected against bootstrap alone (STO-3811).
		if (deferredAvailabilityPending) return
		if (!resolved.some((s) => s.ref === selectedMenuSchedule.ref)) {
			if (resolved[0]) {
				dispatch(initMenuSchedule(resolved[0]))
			}
		}
	}, [
		selectedMenuSchedule,
		filteredBootstrapSchedules,
		headerSchedules,
		deferredAvailabilityPending,
		dispatch,
	])

	// Re-run calculate once per mount when the cart has items and an order type is set.
	// Covers two cases: page refresh (calculations not persisted) and back-navigation from
	// checkout (calculations preserved as stale-while-revalidate so no $0 flash occurs).
	const didCalculateOnMount = useRef(false)
	useEffect(() => {
		if (didCalculateOnMount.current) return
		if (cartItems.length === 0) return
		if (!selectedOrderTypeGuid) return
		if (!location) return

		didCalculateOnMount.current = true
		void dispatch(calculate())
	}, [cartItems.length, selectedOrderTypeGuid, location, dispatch])

	const prevScheduleRef = useRef<MenuSchedule | undefined>(undefined)
	const deferredScheduleSwitchPendingRef = useRef(false)
	const cartItemCount = cartItems.length
	useEffect(() => {
		if (!selectedMenuSchedule) return
		if (prevScheduleRef.current?.ref === selectedMenuSchedule.ref) return
		const prev = prevScheduleRef.current
		prevScheduleRef.current = selectedMenuSchedule

		// Skip on initial mount — no schedule switch has occurred yet.
		if (prev === undefined) return
		if (cartItemCount === 0) return
		// Skip when the URL filter displaced the previous selection — this is not a
		// user-initiated switch, so prompting to clear the cart would be wrong and
		// reverting would re-trigger the URL reconciliation effect in a loop.
		if (urlScheduleRefs.size > 0 && !urlScheduleRefs.has(prev.ref)) return
		// Skip when the previous schedule is absent from a non-empty headerSchedules — this is an
		// eviction (API or off-hours filter excluded the stored selection), not a user action.
		// Prompting would either loop (cancel → setMenuSchedule(prev) → eviction again) or force
		// an unwanted cart clear for a system-driven correction (STO-3817).
		if (headerSchedules.length > 0 && !headerSchedules.some((s) => s.ref === prev.ref)) return
		// For deferred orders, schedules are API-validated for the same future time — no
		// dialog needed. Mark a pending check so the items effect can silently remove any
		// cart items absent from the new schedule's menu (STO-3817).
		// !isDineIn is load-bearing: setOrderType does not clear isDeferred, so switching an
		// in-progress deferred order to dine-in leaves both flags set. headerSchedules falls back to
		// bootstrap for dine-in and the availability query is disabled, so without this guard the
		// branch would treat unvalidated bootstrap schedules as API-confirmed and silently drop cart
		// items instead of prompting. Dine-in falls through to the confirm dialog below.
		if (
			isDeferred &&
			!isDineIn &&
			headerSchedules.some((s) => s.ref === selectedMenuSchedule.ref)
		) {
			// No local hours validation here, matching selectIsScheduleAvailable: membership in
			// headerSchedules already means availability/schedules returned this schedule for the
			// order time, and bootstrap hours cannot describe a future window anyway — only the
			// currently-live schedule has them populated, so a non-active schedule always arrives
			// with hours: [] and any local check would reject every deferred order (STO-3817).
			// A missing expectedTimeUtc is the one genuinely invalid state: reset to ASAP so Order
			// Settings prompts for a valid time.
			if (expectedTimeUtc === null) {
				dispatch(setSchedule({ isDeferred: false }))
				return
			}
			deferredScheduleSwitchPendingRef.current = true
			return
		}

		if (window.confirm(t('component:categoryNav:menuScheduleSwitch:confirm'))) {
			// clearCart() resets itemsRemovedCount to 0 — setItemsRemovedCount must be
			// dispatched immediately after to restore the count for the banner. Do not
			// insert any async operations between these two dispatches (STO-3330).
			dispatch(clearCart())
			dispatch(setItemsRemovedCount(cartItemCount))
		} else {
			// User cancelled — revert to the previous schedule.
			prevScheduleRef.current = prev
			dispatch(setMenuSchedule(prev))
		}
	}, [
		selectedMenuSchedule,
		cartItemCount,
		t,
		dispatch,
		urlScheduleRefs,
		isDeferred,
		isDineIn,
		expectedTimeUtc,
		headerSchedules,
	])

	// iOS keyboard opens: visualViewport.offsetTop shifts away from 0 — patch style.top directly to keep header pinned.
	// window.visualViewport is undefined in jsdom — this effect cannot be unit-tested; verify manually on iOS Safari.
	useEffect(() => {
		const vv = window.visualViewport
		if (!vv) return
		const update = () => {
			if (!menuHeaderRef.current) return
			menuHeaderRef.current.style.top = `${vv.offsetTop}px`
		}
		update()
		vv.addEventListener('scroll', update)
		vv.addEventListener('resize', update)
		return () => {
			vv.removeEventListener('scroll', update)
			vv.removeEventListener('resize', update)
		}
	}, [])

	const calculateErrors = useAppSelector(selectCalculateErrors)
	const calculateError = calculateErrors?.[0] ?? null
	const calculateErrorMessage = calculateError
		? calculateError.friendlyError?.trim() || t(toI18nKey('olo:menu', calculateError.code))
		: null

	// V1 parity (category-tabs.js): show when dine-in order type AND selected schedule is duringOffHours.
	// dineInAllowDuringOffHours is deliberately not checked here, but since STO-3834 it no longer
	// "only gates checkout" — it also filters headerSchedules, which feeds this condition:
	//   - flag false with at least one in-hours schedule: the eviction effect above replaces an
	//     off-hours selection, so this banner does not fire.
	//   - flag false with every schedule off-hours: headerSchedules is [], no replacement is
	//     dispatched, a restored off-hours selection survives and the banner DOES fire.
	// So the banner stays reachable when the flag is off — do not treat it as dead code.
	const showOffHoursWarning =
		selectedOrderTypeGuid === 'DINE_IN' && (selectedMenuSchedule?.duringOffHours ?? false)

	// When a deferred non-dine-in session has no available schedules, the chosen time
	// is no longer bookable. Redirect to Order Settings so the customer can pick a new time.
	useEffect(() => {
		if (!isDeferred || isDineIn) return
		if (!availableSchedulesData) return
		if (availableSchedulesData.schedules.length > 0) return
		dispatch(setStoreSettingsChangedError('orderSettings:scheduleUnavailableError'))
		void navigate(PATHS.orderSettings(ref), { replace: true })
	}, [isDeferred, isDineIn, availableSchedulesData, dispatch, navigate, ref])

	const { data: categoriesData, errorKey: categoriesErrorKey } = useMenuCategories(
		ref,
		scheduleId,
	)
	const { data: orderAgainData } = useOrderAgain(ref, scheduleId, !!dineInTableName)

	// When the user switches between overlapping deferred schedules, silently remove any
	// cart items not available in the new schedule's menu (STO-3817). Runs after
	// categoriesData resolves for the new scheduleId so the availability check is accurate.
	useEffect(() => {
		if (!deferredScheduleSwitchPendingRef.current) return
		if (!categoriesData) return
		deferredScheduleSwitchPendingRef.current = false
		const availableRefs = new Set(categoriesData.categories.flatMap((c) => c.items))
		const itemsToRemove = cartItems.filter((item) => !availableRefs.has(item.itemRef))
		if (itemsToRemove.length === 0) return
		const refsToRemove = [...new Set(itemsToRemove.map((item) => item.itemRef))]
		refsToRemove.forEach((itemRef) => dispatch(removeItemsByMenuRef(itemRef)))
		dispatch(setItemsRemovedCount(itemsToRemove.length))
		void dispatch(calculate())
	}, [categoriesData, cartItems, dispatch])

	const enrichedCategories = useMemo((): MenuCategory[] => {
		const base = categoriesData?.categories ?? []
		if (!orderAgainData?.pastOrderItems.length) return base
		const virtual: MenuCategory = {
			ref: 'order-again',
			name: t('page:menu:orderAgain:name'),
			description: null,
			items: [],
		}
		return [virtual, ...base]
	}, [categoriesData?.categories, orderAgainData?.pastOrderItems.length, t])

	function handleClearSearch() {
		clearSearchRef.current?.()
		setSearchTerm('')
	}

	const childContext: MenuLayoutContext = {
		location,
		scheduleId,
		searchTerm,
		onClearSearch: handleClearSearch,
		isStoreClosureActive,
		hasSameDayClosure,
	}

	if (SCHEDULE_UNAVAILABLE_KEYS.has(categoriesErrorKey ?? '') && !showStoreClosed) {
		return <NoActiveSchedule />
	}

	const activeStoreClosureMessage = outletContext?.activeStoreClosureMessage ?? null
	const locationAddress = location?.addressLine1
		? [location.addressLine1, location.city, location.state].filter(Boolean).join(', ')
		: undefined
	const storeClosedMessage = isStoreClosureActive
		? activeStoreClosureMessage
		: (location?.closureReason ?? null)

	return (
		<>
			{/* Outer grid — CartSidebar starts from the top alongside the header (matches Figma desktop) */}
			<LayoutGrid data-testid="menu-layout-grid">
				{/* Left column: header → nav → page content */}
				<div>
					<StickyHeader ref={menuHeaderRef} isCompact={isCompact}>
						<MenuHeader
							location={outletContext?.location}
							schedules={headerSchedules}
							isCompact={isCompact}
						/>
						<CategoryNav
							scheduleId={scheduleId}
							categories={enrichedCategories}
							onSearch={(term) => setSearchTerm(term.trim())}
							clearSearchRef={clearSearchRef}
							searchTerm={searchTerm}
						/>
					</StickyHeader>
					<MainContent>
						{showOffHoursWarning && (
							// role="status"/polite, not alert/assertive: the menu stays fully
							// browsable and orderable, so this is an advisory notice rather than
							// an error demanding immediate attention.
							<OffHoursWarningBanner role="status" aria-live="polite">
								<ErrorIcon fontSize="small" aria-hidden="true" />
								<OffHoursWarningText>
									{t('component:categoryNav:offHoursWarning')}
								</OffHoursWarningText>
							</OffHoursWarningBanner>
						)}
						{calculateErrorMessage && (
							<CalculateErrorBanner role="alert">
								<ErrorIcon fontSize="small" aria-hidden="true" />
								<CalculateErrorText>{calculateErrorMessage}</CalculateErrorText>
							</CalculateErrorBanner>
						)}
						{/* MenuPage is always mounted so items render regardless of child route.
						    Overlay routes (order-settings, add-item, etc.) render via Outlet on top. */}
						<MenuPage
							scheduleId={scheduleId}
							searchTerm={searchTerm}
							onClearSearch={handleClearSearch}
							currency={location?.currency}
						/>
						<Outlet context={childContext} />
					</MainContent>
				</div>

				{/* Right column: sticky wrapper constrains the cart to viewport height so
				    only the items list scrolls — header and footer stays fixed. */}
				<CartSidebarSticky>
					<CartSidebar
						scheduleId={scheduleId}
						timeZoneOffset={location?.timeZoneOffset}
						currency={location?.currency}
					/>
				</CartSidebarSticky>
			</LayoutGrid>

			{/* Tablet floating bottom bar — visible at md (721px–1024px), hidden at lg where sidebar takes over */}
			<CartBottomBar
				scheduleId={scheduleId}
				timeZoneOffset={location?.timeZoneOffset}
				currency={location?.currency}
			/>

			{hasInvalidUrlSchedule && !showStoreClosed && location && (
				<StoreClosed
					locationName={t('component:scheduleUnavailable:header')}
					address={locationAddress}
					logoUrl={location.logoUrl}
					message={t('api:olo:menu:SCHDNA:error')}
					statusIcon={<ScheduleUnavailableStatusIcon aria-hidden />}
					statusText={t('component:scheduleUnavailable:status')}
				/>
			)}

			{showStoreClosed && location && (
				<StoreClosed
					locationName={
						location.name
							? t('component:storeClosed:header:withName', {
									locationName: location.name,
								})
							: undefined
					}
					address={locationAddress}
					logoUrl={location.logoUrl}
					message={storeClosedMessage}
				/>
			)}
		</>
	)
}
